import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Mail } from 'lucide-react';
import API from '../lib/api';

const STORAGE_KEY = 'sg_chat_session_id';
const NAME_KEY = 'sg_chat_visitor';

const LiveChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => (typeof window !== 'undefined' ? window.localStorage?.getItem(STORAGE_KEY) : null));
  const [visitor, setVisitor] = useState(() => {
    try { return JSON.parse(window.localStorage?.getItem(NAME_KEY) || '{}'); } catch { return {}; }
  });
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', message: '' });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  // Poll messages when chat is open + session exists
  useEffect(() => {
    if (!open || !sessionId) return;
    const poll = async () => {
      try {
        const { data } = await API.get(`/chat/${sessionId}`);
        setMessages(data.messages || []);
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 6000);
    return () => clearInterval(pollRef.current);
  }, [open, sessionId]);

  // Auto scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const startChat = async (e) => {
    e.preventDefault();
    setStarting(true);
    setError('');
    try {
      const { data } = await API.post('/chat/start', form);
      window.localStorage?.setItem(STORAGE_KEY, data.session_id);
      window.localStorage?.setItem(NAME_KEY, JSON.stringify({ first_name: form.first_name, last_name: form.last_name, email: form.email }));
      setSessionId(data.session_id);
      setVisitor({ first_name: form.first_name, last_name: form.last_name, email: form.email });
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to start chat. Try again.');
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setSending(true);
    const optimistic = { id: `local-${Date.now()}`, role: 'user', content: draft.trim(), created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    try {
      await API.post(`/chat/${sessionId}/message`, { content: optimistic.content });
    } catch (err) {
      setError('Message failed to send.');
    } finally {
      setSending(false);
    }
  };

  const endChat = () => {
    window.localStorage?.removeItem(STORAGE_KEY);
    setSessionId(null);
    setMessages([]);
    setForm({ first_name: '', last_name: '', email: '', message: '' });
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[1100] inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-5 py-3 shadow-[0_18px_40px_rgba(16,185,129,0.35)] transition group"
        >
          <MessageCircle size={18} />
          <span className="hidden sm:inline">Live Chat</span>
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-pulse" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-[1100] w-[92vw] max-w-[380px] max-h-[75vh] rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-[#062017] to-[#04160D] shadow-[0_30px_70px_rgba(0,0,0,0.55)] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-emerald-500/15 bg-emerald-950/40">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center text-emerald-950">
                <MessageCircle size={16} />
              </div>
              <div className="min-w-0">
                <div className="text-white text-[13px] font-semibold">SkywayGreen Support</div>
                <div className="text-emerald-300/80 text-[10.5px]">Typical reply within a few minutes</div>
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              className="text-emerald-100/60 hover:text-white transition"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          {!sessionId ? (
            <form onSubmit={startChat} className="p-4 space-y-3 overflow-y-auto">
              <div className="text-emerald-100/70 text-[13px]">Please share a few details so a specialist can help you.</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  data-testid="chat-first-name"
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="First name"
                  className="dark-input text-sm"
                />
                <input
                  data-testid="chat-last-name"
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Last name"
                  className="dark-input text-sm"
                />
              </div>
              <input
                data-testid="chat-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                className="dark-input text-sm w-full"
              />
              <textarea
                data-testid="chat-first-message"
                required
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help you today?"
                className="dark-input text-sm w-full resize-none"
              />
              {error && <div className="text-red-300 text-[12px]">{error}</div>}
              <button
                data-testid="chat-start-btn"
                type="submit"
                disabled={starting}
                className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-semibold text-sm py-2.5 transition"
              >
                {starting ? 'Starting chat…' : 'Start Chat'}
              </button>
              <div className="text-[11px] text-emerald-100/50 pt-1 border-t border-emerald-900/40">
                Prefer email? Write to <a href="mailto:support@skywaygreen.com" className="text-emerald-300 hover:underline">support@skywaygreen.com</a>
              </div>
            </form>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#04120c]" style={{ maxHeight: 'calc(75vh - 160px)' }}>
                {messages.length === 0 && (
                  <div className="text-center text-emerald-100/40 italic text-[12px] py-4">Waiting for messages…</div>
                )}
                {messages.map((m) => (
                  <Bubble key={m.id} m={m} />
                ))}
              </div>
              <form onSubmit={sendMessage} className="p-3 border-t border-emerald-500/15 bg-emerald-950/30 flex gap-2">
                <input
                  data-testid="chat-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  className="dark-input text-sm flex-1"
                  disabled={sending}
                />
                <button
                  data-testid="chat-send"
                  type="submit"
                  disabled={sending || !draft.trim()}
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-emerald-950 text-sm font-semibold px-3"
                >
                  <Send size={14} />
                </button>
              </form>
              <div className="px-3 py-2 border-t border-emerald-900/40 flex items-center justify-between text-[10.5px] text-emerald-100/50 bg-black/30">
                <span>Hi, {visitor.first_name}!</span>
                <div className="flex items-center gap-3">
                  <a href="mailto:support@skywaygreen.com" className="inline-flex items-center gap-1 hover:text-emerald-300">
                    <Mail size={11} /> support@skywaygreen.com
                  </a>
                  <button onClick={endChat} className="hover:text-red-300">End chat</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

const Bubble = ({ m }) => {
  const isUser = m.role === 'user';
  const isBot = m.role === 'bot';
  const align = isUser ? 'items-end' : 'items-start';
  const style = isUser
    ? 'bg-emerald-500 text-emerald-950'
    : isBot
    ? 'bg-amber-500/10 border border-amber-500/40 text-amber-100'
    : 'bg-emerald-900/40 text-emerald-50 border border-emerald-500/25';
  const label = isUser ? 'You' : isBot ? 'SkywayGreen Bot' : 'SkywayGreen Specialist';
  return (
    <div className={`flex flex-col ${align}`}>
      <div className="text-[9.5px] uppercase tracking-widest text-emerald-100/40 mb-0.5">{label}</div>
      <div className={`rounded-2xl px-3 py-2 text-[13px] leading-snug max-w-[85%] whitespace-pre-wrap ${style}`}>{m.content}</div>
      <div className="text-[9px] text-emerald-100/30 mt-0.5">
        {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

export default LiveChatWidget;
